/**
 * Organizations list.
 */

import { Form, Link, useLoaderData, useNavigation, useSearchParams } from 'react-router-dom'
import type { ActionFunctionArgs, LoaderFunctionArgs } from 'react-router-dom'
import type { OrganizationListResult } from '@erp/contracts'
import { api } from '../api'
import type { FieldRegistry, SavedView } from '@erp/contracts'
import { usePagedRows } from '../paging'
import { filtersParam } from '../view-state'
import { ViewBar, handleViewIntent } from '../components/view-bar'
import { Button, EmptyState, PageHeader, SearchInput, Spinner, Table, Toolbar } from '../ui'
import { formatDate } from '../format'

function queryFrom(request: Request) {
  const url = new URL(request.url)
  return {
    search: url.searchParams.get('q') ?? undefined,
    ownerId: url.searchParams.getAll('owner'),
    includeArchived: url.searchParams.get('archived') === 'true',
    filters: filtersParam(url.searchParams),
    cursor: url.searchParams.get('cursor') ?? undefined,
    limit: 50,
  }
}

interface ListData {
  page: OrganizationListResult
  registry: FieldRegistry
  views: SavedView[]
}

async function fetchPage(request: Request): Promise<OrganizationListResult> {
  return api.organizations.list(queryFrom(request))
}

/** Fetcher-only route: just the page, which is what usePagedRows expects. */
export const pageLoader = ({ request }: LoaderFunctionArgs) => fetchPage(request)

export async function loader({ request }: LoaderFunctionArgs): Promise<ListData> {
  const [page, registry, views] = await Promise.all([
    fetchPage(request),
    api.registry('organization'),
    api.views.forEntity('organization'),
  ])
  return { page, registry, views }
}

export async function action({ request }: ActionFunctionArgs) {
  const form = await request.formData()
  const result = await handleViewIntent(String(form.get('intent')), form)
  return result ?? { ok: false, message: 'Unknown action' }
}

export default function Organizations() {
  const { page, registry, views } = useLoaderData() as ListData
  const navigation = useNavigation()
  const [searchParams] = useSearchParams()
  const { rows, cursor, loading, loadMore } = usePagedRows(page, '/crm/organizations/page')

  return (
    <>
      <PageHeader
        title="Organizations"
        actions={
          <Link to="/crm/organizations/new" className="ax-btn ax-btn-primary">
            Create
          </Link>
        }
      />

      <ViewBar entity="organization" registry={registry} views={views} />

      <Toolbar>
        <Form method="get" role="search">
          <SearchInput
            name="q"
            defaultValue={searchParams.get('q') ?? ''}
            placeholder="Search organizations"
          />
        </Form>
        <span className="ax-toolbar-count">
          {page.totalCount} {page.totalCount === 1 ? 'organization' : 'organizations'}
        </span>
        {navigation.state === 'loading' ? <Spinner /> : null}
      </Toolbar>

      {rows.length === 0 ? (
        <EmptyState title="No organizations match this view" />
      ) : (
        <>
          <Table>
            <thead>
              <tr>
                <th scope="col">Name</th>
                <th scope="col">Website</th>
                <th scope="col">City</th>
                <th scope="col">Employees</th>
                <th scope="col">Last modified</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((organization) => (
                <tr key={organization.id}>
                  <td>
                    <Link to={`/crm/organizations/${organization.id}`}>{organization.name}</Link>
                  </td>
                  <td>{organization.website ?? '—'}</td>
                  <td>{organization.city ?? '—'}</td>
                  <td>{organization.noOfEmployees ?? '—'}</td>
                  <td>{formatDate(organization.updatedAt)}</td>
                </tr>
              ))}
            </tbody>
          </Table>

          {cursor ? (
            <div className="ax-list-footer">
              <Button onClick={loadMore} disabled={loading}>
                {loading ? 'Loading…' : 'Load more'}
              </Button>
            </div>
          ) : null}
        </>
      )}
    </>
  )
}

